import { ReactNode, useState } from 'react';
import { Space } from 'antd';
import { isMobile as isMobileQuery } from 'admin/utils/media-queries';
import { Text } from 'admin/components/common/Text';
import { TextField } from 'admin/components/common/Form/TextField';
import { PopConfirm } from 'admin/components/common/PopConfirm/Default';
import { sleep } from 'utils/helpers';

type StrictConfirmProps = {
	title?: string;
	description?: string;
	confirmLabel?: string;
	// The value that needs to be typed to confirm the action
	confirmValue: string;
	onConfirm: () => Promise<unknown> | unknown;
	confirmButtonText?: string;
	successText?: string;
	successTimeout?: number;
	children: ReactNode;
};

export function StrictPopConfirm({
	title,
	description,
	confirmLabel = 'Please type to confirm',
	confirmValue,
	onConfirm,
	confirmButtonText = 'Confirm',
	successText = 'Success!',
	successTimeout = 3_000,
	children,
}: StrictConfirmProps) {
	const [value, setValue] = useState('');
	const [isSuccess, setIsSuccess] = useState(false);
	const [isPending, setIsPending] = useState(false);
	const isMobile = isMobileQuery();

	const handleConfirm = async () => {
		setIsPending(true);
		try {
			await onConfirm();
			setIsSuccess(true);
			if (successTimeout) {
				await sleep(successTimeout); // show success briefly
			}
		} finally {
			setIsPending(false);
		}
	};

	const isConfirmed = value === confirmValue;
	const isDisabled = isPending || !isConfirmed;

	return (
		<PopConfirm
			title={isSuccess ? null : title}
			description={() => (
				<Space direction="vertical" size="small" style={isMobile ? { width: '100%' } : { width: 385 }}>
					{isSuccess ? (
						<Text text={successText} size={Text.size.body} />
					) : (
						<>
							{description && <Text text={description} size={Text.size.body} />}
							<TextField
								label={confirmLabel}
								isRequired
								isLabelUppercase={false}
								onChange={e => setValue(e.target.value)}
								value={value}
							/>
						</>
					)}
				</Space>
			)}
			onConfirm={isDisabled ? undefined : handleConfirm}
			okButtonProps={{
				disabled: isDisabled,
				danger: true,
				loading: isPending,
				size: 'middle',
				block: true,
				hidden: isSuccess,
			}}
			okText={confirmButtonText}
			showCancel={false}
			icon={false}
			onOpenChange={open => !open && setValue('')}
		>
			{children}
		</PopConfirm>
	);
}
